import React, { Component, createRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toggleColumnVisibility } from "../../actions/customColumnActions";

import { DropDownCheckboxSelector } from "../DropDownCheckboxSelector";

import { FilterableTable } from "./SortableTable";

import {
	resetTableColumnWidths,
	setTableTabFilter,
	setTableColumnFilters,
} from "../../actions/tableSortingActions";
import EmptyPaginationComponent from "../EmptyPagination";
import { TabLookingRadioButtonsWithIcon } from "../TabLookingRadioButtons";
import { setFilteredCount } from "../../actions/globalPaginationActions";
import ButtonLink from "../ButtonLink";
import { makeDebouncedFunction } from "../../utilities/utilities";
import { IconColumns3 } from "@tabler/icons-react";
import ExportToCSV from "../ExportToCSV/ExportToCSV";
import { getColumnFilterValue } from "./tableFilterUtils";

/*
	ReactTable wrapper with selector for custom columns, and optional "tabs" interface
	Includes controlled filter state for the react-table

*/

class CustomizableTableInner extends Component {
	editableKeyToFocus = createRef(null);
	cursorRef = createRef(0);
	exportRef = createRef(null);
	constructor(props, context) {
		super(props, context);

		this.state = {
			activeFilters: [],
		};

		this.columnCheckboxSelected = this.columnCheckboxSelected.bind(this);
		this.switchedTab = this.switchedTab.bind(this);
		this.getLeftSideComponent = this.getLeftSideComponent.bind(this);
		this.getButtonsGroup = this.getButtonsGroup.bind(this);
		this.getTabFilteredData = this.getTabFilteredData.bind(this);
		this.getClearFilterButton = this.getClearFilterButton.bind(this);
		// debounce the filter change callback to prevent excessive re-renders
		this.filtersChangedCallback = makeDebouncedFunction(
			this.filtersChangedCallback.bind(this),
			200
		);
	}
	componentDidMount() {
		/* 
			On mount, the excludeTabsFromPersistence array defines
			tabs that the component should not "start" on.  It will
			put the user on the default tab
		*/
		const persistedTab = this.props.tabFilterName;
		const isExcluded =
			persistedTab &&
			this.props.excludeTabsFromPersistence &&
			this.props.excludeTabsFromPersistence.includes(persistedTab);

		const searchParams = new URLSearchParams(window.location.search);
		const tabParam = searchParams.get("tableTabFilter");

		if (tabParam) {
			// Single iteration: find matching tab (case-insensitive)
			const matchingTab = Object.keys(this.props.tabsFilterMap).find(
				(tabName) => tabName.toLowerCase() === tabParam.toLowerCase()
			);

			if (matchingTab) {
				this.props.setTableTabFilter(
					this.props.tableUniqueID,
					matchingTab
				);
			}
		} else if (isExcluded) {
			this.props.setTableTabFilter(
				this.props.tableUniqueID,
				this.props.defaultTabFilterValue
			);
		}

		this.props.tabFilterChanged &&
			this.props.tabFilterChanged(this.props.tabFilterName);

		if (this.props.resolveData) {
			this.props.resolveData(this.getTabFilteredData());
		}
	}
	componentDidUpdate(prevProps) {
		if (this.props.resolveData) {
			if (
				prevProps.data !== this.props.data ||
				prevProps.tabFilterName !== this.props.tabFilterName
			) {
				this.props.resolveData(this.getTabFilteredData());
			}
		}
	}
	columnCheckboxSelected(item) {
		this.props.toggleColumnVisibility(this.props.tableUniqueID, item);
	}
	switchedTab(newTabTitle) {
		this.props.setTableTabFilter(this.props.tableUniqueID, newTabTitle);
		this.props.tabFilterChanged && this.props.tabFilterChanged(newTabTitle);
	}
	getLeftSideComponent() {
		if (this.props.tabsFilterMap) {
			return (
				<TabLookingRadioButtonsWithIcon
					iconComponent={this.props.leftSideIconComponent}
					columns={Object.keys(this.props.tabsFilterMap)}
					switchedTab={this.switchedTab}
					activeTabName={this.props.tabFilterName}
					tabDescriptions={this.props.tabDescriptions}
					customTabNames={this.props.customTabNames}
				/>
			);
		}
		return this.props.leftSideFilterSpace;
	}

	filtersChangedCallback(newFilters) {
		this.props.setTableColumnFilters({
			tableUniqueID: this.props.tableUniqueID,
			filterTextValues: newFilters,
		});
		if (this.props.onFilteredChange) {
			this.props.onFilteredChange(newFilters);
		}
	}

	getNewTableSuperHeader() {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					width: "100vw",
				}}
			>
				<TabLookingRadioButtonsWithIcon
					iconComponent={this.props.leftSideIconComponent}
					columns={Object.keys(this.props.tabsFilterMap)}
					switchedTab={this.switchedTab}
					activeTabName={this.props.tabFilterName}
					tabDescriptions={this.props.tabDescriptions}
					customTabNames={this.props.customTabNames}
				/>
				{this.props.lifecycleStatusToggles()}
				{this.getButtonsGroup({ v2: true })}
			</div>
		);
	}

	getClearFilterButton() {
		return this.state.activeFilters.length ? (
			<div
				style={{
					paddingTop: "10px",
					marginRight: "16px",
				}}
			>
				<ButtonLink
					onClick={() =>
						this.setState({ activeFilters: [] }, () => {
							this.props.setFilteredCount(-1);
							this.filtersChangedCallback(null);
						})
					}
				>
					✕ Clear Filters
				</ButtonLink>
			</div>
		) : null;
	}
	getTabFilteredData() {
		if (
			this.props.tabsFilterMap &&
			this.props.tabsFilterMap[this.props.tabFilterName]
		) {
			return this.props.data.filter(
				this.props.tabsFilterMap[this.props.tabFilterName]
			);
		} else {
			return this.props.data;
		}
	}
	getTextFilteredData() {
		let data = this.getTabFilteredData();
		if (this.state.activeFilters && this.state.activeFilters.length > 0) {
			this.state.activeFilters.forEach((filter) => {
				if (filter.value) {
					const search = filter.value.toLowerCase();
					const column = this.props.columns.find(
						(c) => c.accessor === filter.id || c.id === filter.id
					);
					data = data.filter((row) => {
						const val = getColumnFilterValue(
							column,
							row,
							row[filter.id]
						);
						return (
							val != null &&
							("" + val).toLowerCase().includes(search)
						);
					});
				}
			});
		}
		return data;
	}

	getButtonsGroup({ v2 } = {}) {
		return (
			<div className={v2 ? "buttonGroupV2" : "buttonGroup"}>
				{this.getClearFilterButton()}
				{this.props.actionButtons
					? this.props.actionButtons({
							openCsvExport: () =>
								this.exportRef.current &&
								this.exportRef.current.open(),
						})
					: null}
				{this.props.hideColumnsButton ? null : (
					<DropDownCheckboxSelector
						title="Columns"
						iconComponent={
							<IconColumns3
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						}
						pullRight
						nonCheckboxItems={[
							{
								text: "Reset Column Widths",
								onSelect: () =>
									this.props.resetTableColumnWidths(
										this.props.tableUniqueID
									),
							},
						]}
						checkboxItems={this.props.columns
							.filter((v) => v.accessor !== "actions")
							.filter((v) => !v.unhidable)
							.filter((v) => {
								if (this.props.hiddenColumns) {
									return !this.props.hiddenColumns.includes(
										v.accessor
									);
								} else {
									return true;
								}
							})
							.map((v) => ({
								text: v.Header,
								accessor: v.accessor,
								onSelect: this.columnCheckboxSelected,
								selected:
									this.props.selectedColumns[v.accessor] !==
									false,
							}))}
					/>
				)}
			</div>
		);
	}

	buildPrefixCheckboxItems() {
		const items = [];
		if (this.props.onToggleShowTrash) {
			items.push({
				text: "Include Items in the Trash",
				selected: this.props.showTrash,
				onSelect: this.props.onToggleShowTrash,
				disabled: this.props.showTrashToggleDisabled,
			});
		}
		if (this.props.onToggleShowArchive) {
			items.push({
				text: "Include Archived Items",
				selected: this.props.showArchive,
				onSelect: this.props.onToggleShowArchive,
				disabled: this.props.showArchiveToggleDisabled,
			});
		}
		if (this.props.onToggleFoldersOnTop) {
			items.push({
				text: "Folders on Top (A-Z)",
				selected: this.props.foldersOnTop,
				onSelect: this.props.onToggleFoldersOnTop,
			});
		}
		return items;
	}

	render() {
		return (
			<div
				style={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div style={{ display: "flex", marginTop: 8 }}>
					{this.getLeftSideComponent()}

					<div className="buttonGroup">
						{this.getClearFilterButton()}
						{this.props.actionButtons
							? this.props.actionButtons({
									openCsvExport: () =>
										this.exportRef.current &&
										this.exportRef.current.open(),
								})
							: null}
						{this.props.hideColumnsButton ? null : (
							<DropDownCheckboxSelector
								title="Show"
								iconComponent={
									<IconColumns3
										size={16}
										style={{ verticalAlign: "sub" }}
									/>
								}
								pullRight
								prefixCheckboxItems={this.buildPrefixCheckboxItems()}
								nonCheckboxItems={[
									{
										text: "Reset Column Widths",
										onSelect: () =>
											this.props.resetTableColumnWidths(
												this.props.tableUniqueID
											),
									},
								]}
								checkboxItems={this.props.columns
									.filter((v) => v.accessor !== "actions")
									.filter((v) => !v.unhidable)
									.filter((v) => {
										if (this.props.hiddenColumns) {
											return !this.props.hiddenColumns.includes(
												v.accessor
											);
										} else {
											return true;
										}
									})
									.map((v) => ({
										text: v.Header,
										accessor: v.accessor,
										onSelect: this.columnCheckboxSelected,
										selected:
											this.props.selectedColumns[
												v.accessor
											] !== false,
									}))}
							/>
						)}
					</div>
				</div>
				<div
					className={
						this.props.noAutoResize
							? ""
							: "autoResizeTableContainer"
					}
				>
					<FilterableTable
						data={this.getTabFilteredData()}
						columns={this.props.columns.filter((v) => {
							if (this.props.hiddenColumns) {
								return !this.props.hiddenColumns.includes(
									v.accessor
								);
							} else {
								return true;
							}
						})}
						columnVisibility={{
							...this.props.selectedColumns,
							actions: true,
						}}
						loading={this.props.loading}
						tableUniqueID={this.props.tableUniqueID}
						className={this.props.className}
						noResultsText={this.props.noResultsText}
						defaultSorted={this.props.defaultSorted}
						onSortedChange={this.props.onSortedChange}
						orderByMethod={this.props.orderByMethod}
						filteringCompleteCallback={
							this.props.filteringCompleteCallback
						}
						defaultPageSize={this.props.defaultPageSize}
						uncontrolledPagination={
							this.props.uncontrolledPagination
						}
						PaginationComponent={
							this.props.PaginationComponent ||
							EmptyPaginationComponent
						}
						style={
							this.props.tableStyle || {
								height: 129, //for some reason, the component will only fill the available height AND have a scrollbar if an absolute height is provided here.  flexGrow tells this to fill the available height of the parent
								flexGrow: 1,
							}
						}
						minRows={
							this.props.minRows !== undefined
								? this.props.minRows
								: 0
						}
						// pass active filters so filteringCompleteCallback knows the last filter state
						activeFilters={this.state.activeFilters}
						filtered={this.state.activeFilters}
						onFilteredChange={(newFiltering) => {
							this.setState(
								{ activeFilters: [...newFiltering] },
								() => {
									this.filtersChangedCallback(newFiltering);
								}
							);
						}}
					/>
				</div>
				{this.props.csvExport ? (
					<ExportToCSV
						ref={this.exportRef}
						allData={this.props.data}
						filteredData={this.getTextFilteredData()}
						columns={this.props.columns}
						extraColumns={this.props.csvExport.extraColumns}
						csvColumnRenderers={
							this.props.csvExport.csvColumnRenderers
						}
						columnVisibility={this.props.selectedColumns}
						fileName={this.props.csvExport.fileName}
					/>
				) : null}
			</div>
		);
	}
}

function CustomizableTable(props) {
	const dispatch = useDispatch();

	const selectedColumns = useSelector(
		(state) => state.customColumns[props.tableUniqueID]
	);
	const persistedTabFilter = useSelector(
		(state) =>
			state.tableSorting[props.tableUniqueID] &&
			state.tableSorting[props.tableUniqueID].tabFilter
	);
	const tabFilterName =
		props.tabFilterOverride ||
		persistedTabFilter ||
		props.defaultTabFilterValue;

	return (
		<CustomizableTableInner
			{...props}
			selectedColumns={selectedColumns}
			tabFilterName={tabFilterName}
			toggleColumnVisibility={(...args) =>
				dispatch(toggleColumnVisibility(...args))
			}
			setTableTabFilter={(...args) =>
				dispatch(setTableTabFilter(...args))
			}
			resetTableColumnWidths={(...args) =>
				dispatch(resetTableColumnWidths(...args))
			}
			setFilteredCount={(...args) => dispatch(setFilteredCount(...args))}
			setTableColumnFilters={(...args) =>
				dispatch(setTableColumnFilters(...args))
			}
		/>
	);
}

export default CustomizableTable;
